import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RefreshCw, Activity, Globe, Clock } from 'lucide-react'
import { apiService } from '../services/api'
import type { DashboardData, CountryData } from '../types'

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const [fetchDataFunction, setFetchDataFunction] = useState<(() => Promise<void>) | null>(null)

  const handleRefresh = async () => {
    if (fetchDataFunction) {
      setRefreshing(true)
      await fetchDataFunction()
    }
  }

  useEffect(() => {
    const fetchData = async (retryCount = 0, isManualRefresh = false) => {
      try {
        // 초기 로딩일 때만 loading을 true로 설정
        if (retryCount === 0 && isInitialLoad) {
          setLoading(true)
        }
        // 수동 새로고침일 때는 refreshing 상태 사용
        if (isManualRefresh) {
          setRefreshing(true)
        }
        setError(null)
        const response = await apiService.getDashboardData()
        
        if (response.success) {
          setDashboardData(response.data)
          // 첫 번째 성공적인 데이터 로드 후 초기 로딩 완료
          if (isInitialLoad) {
            setIsInitialLoad(false)
          }
        } else {
          setError(response.message || '데이터를 불러오는데 실패했습니다.')
        }
      } catch (err) {
        console.error('Dashboard data fetch error:', err)
        let errorMessage = '알 수 없는 오류가 발생했습니다.'
        
        if (err instanceof Error) {
          if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
            errorMessage = '서버와의 연결에 실패했습니다. 네트워크를 확인해주세요.'
          } else if (err.message.includes('서버에 연결할 수 없습니다')) {
            errorMessage = err.message
          } else {
            errorMessage = `API 오류: ${err.message}`
          }
        }
        
        // 최대 3번까지 재시도 (네트워크 에러인 경우)
        if (retryCount < 3 && errorMessage.includes('서버에 연결할 수 없습니다')) {
          console.log(`재시도 중... (${retryCount + 1}/3)`)
          setTimeout(() => fetchData(retryCount + 1, isManualRefresh), 2000) // 2초 후 재시도
          return
        }
        
        setError(errorMessage)
      } finally {
        if (isInitialLoad) {
          setLoading(false)
        }
        setRefreshing(false)
      }
    }

    // fetchData 함수를 상태에 저장하여 handleRefresh에서 사용할 수 있도록 함
    setFetchDataFunction(() => () => fetchData(0, true))
    
    fetchData()
    const interval = setInterval(() => fetchData(), 5000) // 5초마다 새로고침

    return () => clearInterval(interval)
  }, [isInitialLoad])

  const getStatusColor = (status: CountryData['status']) => {
    switch (status) {
      case 'online':
        return 'bg-green-500'
      case 'warning':
        return 'bg-yellow-500'
      case 'offline':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = (status: CountryData['status']) => {
    switch (status) {
      case 'online':
        return '정상'
      case 'warning':
        return '경고'
      case 'offline':
        return '오프라인'
      default:
        return '알 수 없음'
    }
  }

  if (loading && isInitialLoad) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex items-center space-x-4 backdrop-blur-sm bg-black/30 p-8 rounded-2xl border border-purple-500/30">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-400" />
          <span className="text-xl text-white">데이터를 불러오는 중...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Card className="w-96 backdrop-blur-sm bg-black/30 border border-red-500/30">
          <CardHeader>
            <CardTitle className="text-red-400 text-xl">오류 발생</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-purple-200 mb-6">{error}</p>
            <Button 
              onClick={handleRefresh} 
              disabled={refreshing}
              className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white border-0"
            >
              {refreshing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              다시 시도
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <span className="text-xl text-purple-200">데이터가 없습니다.</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto p-6 space-y-8">
        {/* 헤더 */}
        <div className="flex items-center justify-between backdrop-blur-sm bg-black/20 rounded-2xl p-6 border border-purple-500/20">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              아시아 네트워크 모니터링
            </h1>
            <p className="text-purple-200/80 mt-2 text-lg">
              마지막 업데이트: {new Date(dashboardData.lastUpdated).toLocaleString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
                timeZone: 'Asia/Seoul'
              })}
            </p>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing} 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-purple-500/40"
          >
            {refreshing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            새로고침
          </Button>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="backdrop-blur-sm bg-black/30 border border-purple-500/30 hover:border-purple-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-purple-200">전체 타겟</CardTitle>
              <Globe className="h-5 w-5 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{dashboardData.totalTargets}</div>
              <p className="text-sm text-purple-300/80">모니터링 중인 서버</p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-black/30 border border-green-500/30 hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-green-200">온라인 타겟</CardTitle>
              <Activity className="h-5 w-5 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{dashboardData.onlineTargets}</div>
              <p className="text-sm text-green-300/80">
                가용률: {((dashboardData.onlineTargets / dashboardData.totalTargets) * 100).toFixed(1)}%
              </p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-black/30 border border-blue-500/30 hover:border-blue-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-blue-200">평균 응답시간</CardTitle>
              <Clock className="h-5 w-5 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">
                {dashboardData.countries.length > 0
                  ? Math.round(
                      dashboardData.countries.reduce((sum, country) => sum + country.averageResponseTime, 0) /
                        dashboardData.countries.length
                    )
                  : 0}ms
              </div>
              <p className="text-sm text-blue-300/80">전체 평균</p>
            </CardContent>
          </Card>
        </div>

        {/* 국가별 상태 - 카드 그리드 레이아웃 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(() => {
            // 국가 순서 정의: 필리핀, 베트남, 중국, 캄보디아
            const countryOrder = ['필리핀', '베트남', '중국', '캄보디아'];
            const sortedCountries = [...dashboardData.countries].sort((a, b) => {
              const indexA = countryOrder.indexOf(a.country);
              const indexB = countryOrder.indexOf(b.country);
              return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
            });
            
            return sortedCountries.map((country) => (
              <Card key={country.countryCode} className="backdrop-blur-sm bg-black/30 border border-purple-500/30 hover:border-purple-400/50 transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{country.countryFlag}</span>
                      <div>
                        <CardTitle className="text-xl font-bold text-white">{country.country}</CardTitle>
                        <p className="text-sm text-purple-300/70">{country.region}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <Badge className={`${getStatusColor(country.status)} text-white shadow-lg text-xs`}>
                        {getStatusText(country.status)}
                      </Badge>
                      <div className="text-xs text-purple-300">
                        평균: {country.averageResponseTime.toFixed(0)}ms
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {country.targets && country.targets.length > 0 ? (
                      country.targets.map((target) => (
                        <div
                          key={target.id}
                          className="backdrop-blur-sm bg-black/40 border border-purple-500/20 rounded-lg p-3 hover:border-purple-400/40 transition-all duration-300"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-white text-sm truncate">{target.region}</span>
                                <Badge className={`${target.isOnline ? 'bg-green-500' : 'bg-red-500'} text-white text-xs px-1 py-0`}>
                                  {target.isOnline ? '●' : '●'}
                                </Badge>
                              </div>
                              <div className="text-xs text-purple-300 truncate">
                                {target.ipAddress}
                                {target.domain && (
                                  <span className="text-purple-400 ml-1">({target.domain})</span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-1 ml-2">
                              <div className="text-xs text-purple-200">
                                <span className="text-white font-medium">
                                  {target.currentResponseTime ? `${target.currentResponseTime.toFixed(0)}ms` : 'N/A'}
                                </span>
                              </div>
                              <div className="text-xs text-purple-200">
                                <span className="text-white font-medium">{target.successRate.toFixed(1)}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-purple-300/70">
                            마지막 핑: {target.lastPingTime 
                              ? (() => {
                                  const date = new Date(target.lastPingTime);
                                  const utcHours = date.getUTCHours();
                                  const utcMinutes = date.getUTCMinutes();
                                  const utcSeconds = date.getUTCSeconds();
                                  return String(utcHours).padStart(2, '0') + ':' + 
                                         String(utcMinutes).padStart(2, '0') + ':' + 
                                         String(utcSeconds).padStart(2, '0');
                                })()
                              : 'N/A'
                            }
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-purple-300/70 text-sm">
                        활성화된 타겟이 없습니다.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ));
          })()}
        </div>
      </div>
    </div>
  )
}

export default Dashboard